import { Anchor } from './MarketingShell';
import { Band, Card, Boundaries } from './sections';

/**
 * Security and governance, written for the person in a procurement pack who
 * has to sign something.
 *
 * The rule for this page is narrower than for the rest of the site: every
 * statement is either a property of the code that can be pointed at, or an
 * explicit statement that the answer is commercial and will be given in
 * writing. Nothing in between. A security page that reads well and turns out
 * to be aspirational is worse than no security page, because it is the one
 * page a buyer will quote back during a DPIA.
 *
 * Three things are therefore deliberately NOT claimed here, and should not be
 * added without the deployment actually changing:
 *
 *   - a jurisdiction for hosting. The application runs in Frankfurt, and the
 *     database is wherever the project was created. "UK hosting" is a sentence
 *     somebody would rely on.
 *   - a certification. There is no ISO 27001, no DSPT submission and no
 *     Cyber Essentials assessment yet, and naming a standard the product has
 *     not been assessed against is the kind of claim that ends a procurement.
 *   - a signed DPA, a subprocessor list or a penetration test report. Those
 *     exist as commitments a person makes, not as facts about a repository.
 */

/** A labelled fact, for the tables a procurement reader skims rather than reads. */
function Fact({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="mk-fact">
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function SecurityPage() {
  return (
    <>
      <Band
        title="Security and governance"
        heading="h1"
        lede="Written for information governance leads, data protection officers and anyone
              assembling a DPIA. It
              describes what the software does, and says plainly where an answer is a commitment
              rather than a property of the product."
      >
        <div className="mk-two">
          <Card title="The short version">
            <ul className="mk-list">
              <li>No patient-identifiable data, by design and by refusal.</li>
              <li>No connection to any clinical system.</li>
              <li>No clinical advice, and nothing sent to a patient.</li>
              <li>Every extracted item carries a citation, or it is discarded.</li>
              <li>A named person approves anything the organisation relies on.</li>
              <li>Review and approval history cannot be rewritten.</li>
            </ul>
          </Card>
          <Card title="What we will not put on this page">
            <p>
              Hosting jurisdiction, the data processing agreement, the subprocessor list and
              certification status are commercial and legal commitments. We answer them precisely,
              in writing, for the arrangement you would actually be on — rather than publishing a
              sentence that stops being true when the infrastructure changes.
            </p>
            <Anchor to="/contact" className="btn btn-secondary">Ask for them in writing</Anchor>
          </Card>
        </div>
      </Band>

      <Band
        id="data"
        title="What data InBrief holds"
        tone="tint"
        lede="Contracts, service specifications and approved organisational information — plus the
              accounts of the staff who use it. That is the whole list."
      >
        <dl className="mk-facts">
          <Fact term="Patient-identifiable data">
            None. Uploads are scanned before processing and one that appears to contain patient
            identifiers is rejected with the reason rather than quietly accepted. Contracts and
            specifications do not contain patient data, so in normal use this never fires.
          </Fact>
          <Fact term="Clinical systems">
            No connection to EMIS, SystmOne, or any other clinical system, and no integration
            planned that would create one. InBrief cannot tell you which of your patients are
            outstanding on anything, because it has never seen a patient.
          </Fact>
          <Fact term="Personal data we do hold">
            Staff account records — name, work email address, role, and the record of what each
            person approved. This is the data a controller would expect to see in a DPIA, and it
            is the only personal data in the system.
          </Fact>
          <Fact term="Special category data">
            None held, and none processed.
          </Fact>
        </dl>
      </Band>

      <Band
        id="separation"
        title="How one organisation is kept away from another"
        lede="Enforced in the database rather than in application code — so a query that forgot to
              filter returns nothing, instead of returning everything."
      >
        <div className="mk-three">
          <Card title="Row-level security">
            <p>
              Every table carrying customer content is policed on the organisation it belongs to.
              The policies are forced, and the application connects as a role that cannot bypass
              them.
            </p>
          </Card>
          <Card title="Per-request scoping">
            <p>
              The tenant is set for the duration of a single transaction, so a connection reused
              from a pool cannot carry one customer's context into another customer's request.
            </p>
          </Card>
          <Card title="Roles and rights">
            <p>
              Eight roles, each a combination of four rights, enforced on the server rather than by
              hiding buttons. A receptionist can read approved answers and cannot start an
              extraction or approve a communication.
            </p>
          </Card>
        </div>
      </Band>

      <Band id="handling" title="How data is handled" tone="tint">
        <dl className="mk-facts">
          <Fact term="In transit">
            HTTPS throughout, with HSTS set in production.
          </Fact>
          <Fact term="At rest">
            Encryption at rest is provided by the managed database and object storage the
            deployment uses. Uploads to object storage are written with server-side encryption
            enabled by default, and with a customer-managed key where one is configured.
          </Fact>
          <Fact term="Passwords">
            Never stored. Each is put through scrypt at the OWASP-recommended parameters with a
            per-account salt, and compared in constant time.
          </Fact>
          <Fact term="Sessions">
            Cookie-based, HTTP-only, same-site, and marked secure in production, so a session
            cannot be read by scripts in the page or sent to another site.
          </Fact>
          <Fact term="Spend">
            A per-organisation monthly ceiling on model spend is checked before every model call
            and before an extraction is queued. It is a hard stop, not a warning — one customer
            cannot run up an unbounded bill.
          </Fact>
        </dl>
      </Band>

      <Band
        id="models"
        title="Model usage"
        lede="One model provider, used for four features, on documents you chose to upload."
      >
        <div className="mk-two">
          <Card title="What is sent">
            <ul className="mk-list">
              <li>The documents you upload, when they are read.</li>
              <li>The question a member of staff asks, with passages from your approved documents.</li>
              <li>The text of a communication being drafted or translated.</li>
            </ul>
            <p className="mk-small">
              Your documents are not used as training data by us or by the model provider.
            </p>
          </Card>
          <Card title="How answers are kept honest">
            <ul className="mk-list">
              <li>
                Extraction runs in two passes. The first produces citations attached by the model
                provider's API rather than written by the model, because a model asked to report
                its own sources will invent them.
              </li>
              <li>
                An item the system cannot trace back to a passage in the document is discarded.
                The database will not store one without a source.
              </li>
              <li>
                An answer must cite or refuse. A confident, ungrounded answer cannot be written —
                that is a constraint in the database, not a behaviour of the prompt.
              </li>
              <li>Every model call is metered per organisation, per feature.</li>
            </ul>
          </Card>
        </div>
      </Band>

      <Band
        id="audit"
        title="Audit and approval"
        tone="tint"
        lede="The record of who decided what is a compliance artefact, so the software treats it as
              one."
      >
        <dl className="mk-facts">
          <Fact term="Append-only">
            Reviews, approvals, translation reviews and the audit log cannot be updated or deleted.
            That is enforced by database triggers, so a bug — or a later change by someone without
            this context — cannot rewrite history either.
          </Fact>
          <Fact term="Audit records outlive their subjects">
            The audit log carries no foreign keys, deliberately. An audit entry blanked when an
            account closes is not evidence of anything.
          </Fact>
          <Fact term="Approval is by a named person">
            Nothing a patient would read reaches them without a person with the right to approve it
            having done so, recorded against their name and the time.
          </Fact>
          <Fact term="Translations">
            A translation must be approved before it can be used, and cannot be approved unreviewed.
            Independent linguistic review by a qualified translator is available as a paid service.
          </Fact>
          <Fact term="The one exception">
            Erasure and retention expiry can remove records, and doing so is itself audited. A
            system that could never delete anything could not honour an erasure request.
          </Fact>
        </dl>
      </Band>

      <Band id="retention" title="Retention, export and deletion">
        <div className="mk-three">
          <Card title="Retention">
            <p>
              Configurable per organisation, seven years by default, measured from upload. The clock
              uses the newest version, so a reissued contract is current however old version one is.
              Nothing here knows when a contract actually ends, and the interface says so rather
              than implying a precision it does not have.
            </p>
          </Card>
          <Card title="Deletion means deletion">
            <p>
              Deleting a document removes every version, its extractions and the stored files.
              Erasing the index and leaving the contract on disk would not be erasure. On versioned
              storage that means removing every version rather than writing a delete marker.
            </p>
          </Card>
          <Card title="Expiry does not delete by default">
            <p>
              The retention sweep reports what has expired and removes nothing until an
              administrator turns deletion on, having read the reports. Every sweep is recorded,
              including the ones that deleted nothing — so both "why has this gone?" and "why is
              this still here?" have an answer.
            </p>
          </Card>
        </div>
        <p className="mk-small">
          Extracted items export to CSV at any time. You are not locked in to get your own
          contract knowledge back out.
        </p>
      </Band>

      <Band id="hosting" title="Hosting and processors" tone="tint">
        <p className="mk-prose">
          InBrief runs on managed cloud infrastructure in Europe, with a managed PostgreSQL
          database and object storage, and uses a single model provider for the four AI features.
          The exact regions, the processors involved and the terms that bind them belong in a data
          processing agreement rather than on a marketing page, because they are the part a
          controller has to be able to rely on — and the part most likely to change.
        </p>
        <p className="mk-prose">
          Ask, and we will give you the current answer in writing, for the arrangement you would
          actually be on: region, subprocessors, retention configuration, breach notification and
          the DPA itself.
        </p>
        <div className="mk-hero-actions">
          <Anchor to="/contact" className="btn btn-primary">Request the governance pack</Anchor>
        </div>
      </Band>

      <Band id="regulatory" title="Regulatory position">
        <div className="mk-two">
          <Card title="Not a medical device">
            <p>
              InBrief gives no clinical advice, makes no diagnosis, performs no triage, and
              recommends no medication or treatment. It reads contracts and drafts administrative
              communications for a person to approve. Those boundaries are the reason it sits
              outside the medical device regime, which is why they are enforced in the software
              rather than written as a caveat.
            </p>
          </Card>
          <Card title="Not affiliated with the NHS">
            <p>
              InBrief is an independent software product. It is not affiliated with, endorsed by,
              or accredited by the NHS or any of its bodies, and it carries no NHS branding. Where
              it describes a contractual requirement, the contract you uploaded is the authority —
              not InBrief.
            </p>
          </Card>
        </div>
      </Band>

      <Band title="Where the product stops" tone="tint">
        <Boundaries />
        <p className="mk-small mk-boundary-note">
          These are boundaries, not gaps waiting to be filled.
        </p>
      </Band>

      <Band tone="deep">
        <div className="mk-final">
          <h2 className="mk-final-title">Send us your information governance questions.</h2>
          <p className="mk-final-lede">
            We would rather answer a hard question early than discover it in week six of a
            procurement.
          </p>
          <div className="mk-hero-actions">
            <Anchor to="/contact" className="btn btn-primary">Contact us</Anchor>
            <Anchor to="/resources" className="btn btn-secondary">Resources and FAQs</Anchor>
          </div>
        </div>
      </Band>
    </>
  );
}
